import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/db1iok32asjjtt1sl8d0';

function ContactForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // 反垃圾：蜜罐欄位若有值，不送出，僅顯示一般成功回饋。
    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === 'company_alt') return;
        if (typeof value === 'string' && value.trim() === '') return;
        payload.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setFormError(String(serverMsg) || '送出失敗，請稍後再試。');
        setStatus('error');
        return;
      }

      setStatus('success');
      formEl.reset();
    } catch {
      setFormError('網路連線異常，請稍後再試。');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        <p className="flex items-center gap-2 font-semibold">
          <i className="ri-checkbox-circle-line text-lg" />
          已收到你的諮詢需求
        </p>
        <p className="mt-1.5">天地人網將盡快與你聯繫，安排人工深度健診。</p>
      </div>
    );
  }

  return (
    <form id="gmb-contact-form" data-readdy-form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-slate-700">
            聯絡人姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={60}
            className="rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
            placeholder="你的姓名"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-slate-700">
            電子郵件
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={120}
            className="rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-phone" className="text-sm font-medium text-slate-700">
            聯絡電話
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            maxLength={30}
            className="rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
            placeholder="方便聯繫的電話"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-store" className="text-sm font-medium text-slate-700">
            店家名稱
          </label>
          <input
            id="contact-store"
            name="storeName"
            type="text"
            maxLength={80}
            className="rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
            placeholder="想健診的店家名稱"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-slate-700">
          想了解的內容
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          maxLength={500}
          className="resize-none rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none"
          placeholder="例如：想了解同業比較、評論經營或整體曝光改善方向。"
        />
        <p className="text-xs text-slate-400">最多 500 字。</p>
      </div>

      <div className="aux-field-wrap" aria-hidden="true">
        <input
          id="contact-company-alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      {status === 'error' && formError && (
        <p className="flex items-center gap-1.5 text-sm text-rose-600">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:self-end"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            送出中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            送出諮詢
          </>
        )}
      </button>
    </form>
  );
}

export default function CtaSection() {
  const [showForm, setShowForm] = useState(false);

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pt-12 md:px-8 md:pt-14">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex flex-col justify-between rounded-lg border border-slate-200 bg-slate-50 p-5 md:p-6">
          <div>
            <h2 className="text-base font-bold text-slate-900 md:text-lg">重新健診其他店家</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              想比較不同分店或另一間店家嗎？回到搜尋頁輸入名稱與地區，即可重新產生健診。
            </p>
          </div>
          <Link
            to="/gmb-audit"
            className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 sm:w-auto sm:self-start"
          >
            <i className="ri-refresh-line" />
            重新健診
          </Link>
        </div>

        <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-5 md:p-6">
          <h2 className="text-base font-bold text-slate-900 md:text-lg">需要更完整的診斷？</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            公開資料看不到的後台成效，可由天地人網進行人工深度健診，提供完整的經營與優化建議。
          </p>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800 sm:w-auto"
            >
              <i className="ri-customer-service-2-line" />
              聯絡天地人網
            </button>
          )}
          {showForm && <ContactForm />}
        </div>
      </div>
    </section>
  );
}